<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: null,
                    },
                    {
                        name: $tc('messages.duels', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <DuelsDashboardBanner :duels="duelBanner" />

        <div>{{ $t('messages.this_week_results') }}:</div>

        <div class="md:grid md:grid-cols-12 md:gap-6">
            <div v-if="graphs.pie" class="col-span-4 align-middle">
                <div>
                    <apexchart
                        :type="graphs.pie.chart.type"
                        :height="graphs.pie.chart.height"
                        :series="graphs.pie.series"
                        :options="graphs.pie"
                    />
                </div>
            </div>

            <div class="col-span-8">
                <div>
                    <apexchart
                        :type="graphs.bar.chart.type"
                        :height="graphs.bar.chart.height"
                        :series="graphs.bar.series"
                        :options="graphs.bar"
                    />
                </div>
            </div>
        </div>

        <div>
            {{ $t('messages.active_duels') }}
            <span class="ml-1 text-sm">({{ $tc('messages.max_short_X', { max: 5 }) }})</span>
        </div>

        <ul class="md:grid md:grid-cols-3 lg:grid-cols-5 md:gap-6">
            <li v-for="duel in duels" :key="duel.id" class="rounded-2xl bg-gray-100 px-8 py-10">
                <img class="mx-auto h-36 w-36 rounded-full" :src="duel.challenged_avatar" alt="" />
                <h3 class="mt-6 text-base font-semibold leading-7 tracking-tight text-gray-700">
                    {{ duel.challenged_name }}
                </h3>
                <div>
                    <AppConfirmActionButton
                        :href="duel.links.delete"
                        :button-text="$t('messages.cancel')"
                        :confirm-text="$t('messages.cancel')"
                        v-if="duel.links.delete"
                        danger
                        method="delete"
                        :cancel-button-text="$t('messages.no')"
                    />
                </div>
            </li>
            <li class="rounded-2xl bg-gray-100 px-8 py-10" v-if="duels.length < 5">
                <div class="mx-auto h-36 w-36 rounded-full bg-gray-400" />
                <h3 class="mt-6 text-base font-semibold leading-7 tracking-tight text-gray-700">
                    {{ $t('messages.new') }}
                </h3>
                <div>
                    <DuelPartnerSearchPanel :endpoint-url="meta.links.practice_duels_search" />
                </div>
            </li>
        </ul>

        <div>
            <div class="flex flex-col mt-5">
                <div>{{ $t('messages.finished_duels') }}</div>

                <div>
                    <AppWell>
                        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <div v-for="duel in finishedDuels.data" :key="duel.id">
                                <AppCard>
                                    <div class="px-2 py-4">
                                        <div class="flex justify-center">
                                            <span>{{ $tc('messages.duels', 1) }}</span>
                                        </div>
                                        <div class="flex justify-center text-gray-500 text-sm">
                                            <span>{{ duel.date_display }}</span>
                                        </div>
                                        <div>
                                            <div
                                                class="flex justify-center items-center gap-4"
                                                :class="{ 'flex-row-reverse': !duel.student_is_challenger }"
                                            >
                                                <div class="flex flex-col items-center">
                                                    <img
                                                        :src="duel.challenger_avatar"
                                                        :alt="duel.challenger_name"
                                                        :title="duel.challenger_name"
                                                        class="rounded-full h-20"
                                                    />
                                                    <div>{{ duel.challenger_name }}</div>
                                                    <div>
                                                        <BadgePill :color="duel.challenger_status.color">
                                                            {{ duel.challenger_status.display }}
                                                        </BadgePill>
                                                    </div>
                                                    <div>{{ duel.challenger_result }}</div>
                                                </div>
                                                <div><IconVs class="h-20 w-20 text-gray-300" /></div>
                                                <div class="flex flex-col items-center">
                                                    <img
                                                        :src="duel.challenged_avatar"
                                                        :alt="duel.challenged_name"
                                                        :title="duel.challenged_name"
                                                        class="rounded-full h-20"
                                                    />
                                                    <div>{{ duel.challenged_name }}</div>
                                                    <div>
                                                        <BadgePill :color="duel.challenged_status.color">
                                                            {{ duel.challenged_status.display }}
                                                        </BadgePill>
                                                    </div>
                                                    <div>{{ duel.challenged_result }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-if="duel.tie_display_message">
                                            <Tippy>
                                                <button>
                                                    <IconInfoCircle class="w-6 h-6 text-gray-400" />
                                                </button>
                                                <template #content>
                                                    {{ duel.tie_display_message }}
                                                </template>
                                            </Tippy>
                                        </div>
                                        <div class="mt-3">
                                            <InertiaSquareLinkButton action="show" :href="duel.links.show" />
                                        </div>
                                    </div>
                                </AppCard>
                            </div>
                        </div>
                    </AppWell>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="finishedDuels" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import { IconHourglassEmpty, IconInfoCircle, IconVs } from '@tabler/icons-vue';
import AppCard from '@/Shared/AppCard.vue';
import AppWell from '@/Shared/AppWell.vue';
import BadgePill from '@/components/BadgePill.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import DuelPartnerSearchPanel from '@/Pages/Student/Activity/Practice/Duels/partials/DuelPartnerSearchPanel.vue';
import AppConfirmActionButton from '@/components/AppConfirmActionButton.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import DuelsDashboardBanner from '@/Pages/Student/Dashboard/Partials/DuelsDashboardBanner.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import { Tippy } from 'vue-tippy';

export default {
    components: {
        IconInfoCircle,
        Tippy,
        AlertMessage,
        IconHourglassEmpty,
        DuelsDashboardBanner,
        InertiaPagination,
        IconVs,
        AppConfirmActionButton,
        DuelPartnerSearchPanel,
        InertiaSquareLinkButton,
        BadgePill,
        AppWell,
        AppCard,
        PanelPage,
    },
    layout: Layout,
    name: 'Index',
    props: ['graphs', 'filters', 'duels', 'finishedDuels', 'meta', 'duelBanner'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                difficulty: this.filters.difficulty,
            }),
            showAll: false,
        };
    },
};
</script>
